import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `settingsBilling:` scope (F19/F20/F24/F26/
 * F45/F46/F56/F57, `/settings/billing`) — see `../settings.i18n.ts`'s doc
 * comment for the pattern this follows (`driveSettings:` precedent).
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/settingsBilling.json`.
 */
const en = {
  'page.title': 'Plan & Billing',
  'page.description': 'Manage your plan, credits, and payment history.',

  // --- F20/F24 plan card ---
  'plan.title': 'Your plan',
  'plan.free.title': "You're on Free",
  'plan.pro.title': "You're on PRO",
  'plan.pro.renews': 'Renews {date}',
  'plan.pro.cancelsAt': 'PRO until {date}, then moves to Free',
  'plan.pastDue.title': 'Payment past due',
  'plan.pastDue.description': 'Your PRO payment didn’t go through — update your payment method by {date} to keep PRO.',
  'plan.pastDue.descriptionNoDate': 'Your PRO payment didn’t go through — update your payment method to keep PRO.',
  'plan.status.loading': 'Loading your plan…',
  'plan.status.error.title': "Couldn't load your plan.",
  'plan.action.upgrade': 'Upgrade to PRO',
  'plan.action.manage': 'Manage subscription',
  'plan.action.cancel': 'Cancel PRO',
  'plan.action.reactivate': 'Reactivate',
  'plan.action.updatePayment': 'Update payment method',
  'plan.checkoutError': "Couldn't start checkout. Try again.",

  // --- Cancel flow ---
  'cancel.title': 'Cancel PRO',
  'cancel.body': 'You’ll keep PRO access until {date}. After that, your account moves to Free and PRO-only features (unlimited expand, cut-and-continue, difficulty elevation, translation, illustrations) will be locked.',
  'cancel.bodyNoDate': 'Your account will move to Free at the end of the current billing period. PRO-only features will be locked once that happens.',
  'cancel.keepPro': 'Keep PRO',
  'cancel.confirm': 'Cancel PRO',
  'cancel.error': "Couldn't cancel your subscription. Try again.",
  'cancel.toast.action': 'Canceled',

  // --- F19/F47 credits ---
  'credits.title': 'Credits',
  'credits.balance.label': 'Balance',
  'credits.balance.low': 'Your balance is running low.',
  'credits.balance.zero': 'You’re out of credits — buy more to keep generating.',
  'credits.ledger.title': 'Ledger',
  'credits.ledger.column.date': 'Date',
  'credits.ledger.column.reason': 'Reason',
  'credits.ledger.column.delta': 'Amount',
  'credits.ledger.column.balance': 'Balance',
  'credits.ledger.empty.title': 'No credit activity yet',
  'credits.ledger.empty.description': 'Generations, purchases, and rewards will show up here.',
  'credits.ledger.noResults.title': 'No credit activity matches “{query}”',
  'credits.ledger.error.title': "Couldn't load your credit ledger.",
  'credits.ledger.reason.action_debit': 'Generation',
  'credits.ledger.reason.policy_refund': 'Refund',
  'credits.ledger.reason.purchase': 'Purchase',
  'credits.ledger.reason.admin_grant': 'Grant',
  'credits.ledger.reason.share_approved': 'Share approved',
  'credits.ledger.reason.share_public_reward': 'Share reward',
  'credits.ledger.reason.referral_reward': 'Referral reward',
  'credits.ledger.reason.promo_bonus': 'Promo bonus',
  'credits.ledger.reason.signup_grant': 'Welcome credits',

  // --- F57 buy credits ---
  'packs.title': 'Buy credits',
  'packs.status.loading': 'Loading credit packs…',
  'packs.status.error.title': "Couldn't load credit packs.",
  'packs.status.empty.title': 'No credit packs available right now',
  'packs.buy': 'Buy',
  'packs.buying': 'Starting checkout…',

  // --- F56 promo ---
  'promo.label': 'Have a code?',
  'promo.placeholder': 'Enter promo code',
  'promo.apply': 'Apply',
  'promo.remove': 'Remove',
  'promo.validating': 'Checking code…',
  'promo.applied.percent': '−{percent}% applied',
  'promo.applied.fixed': '−{amount} applied',
  'promo.applied.bonus': '+{count, plural, one {# bonus credit} other {# bonus credits}} applied',
  'promo.error.required': 'Enter a code first.',

  // --- F26 invoices ---
  'invoices.title': 'Invoices',
  'invoices.column.date': 'Date',
  'invoices.column.amount': 'Amount',
  'invoices.column.status': 'Status',
  'invoices.column.download': 'Download',
  'invoices.empty.title': 'No invoices yet',
  'invoices.noResults.title': 'No invoices match “{query}”',
  'invoices.error.title': "Couldn't load your invoices.",
  'invoices.download': 'Download',
  'invoices.lockedForFree.title': 'Invoices are available on PRO',
  'invoices.lockedForFree.description': 'Upgrade to PRO to see your billing history.',
  'invoices.status.paid': 'Paid',
  'invoices.status.open': 'Open',
  'invoices.status.void': 'Void',
  'invoices.status.refunded': 'Refunded',

  // --- F48 credits-info explainer (design/app/33-credits-info.html) ---
  'creditsInfo.trigger': 'How credits work',
  'creditsInfo.eyebrow': 'Pricing',
  'creditsInfo.title': 'How credits work',
  'creditsInfo.description': 'Every AI action costs a base amount plus a per-unit rate scaled by complexity. You always see the cost before you confirm.',
  'creditsInfo.column.action': 'Action',
  'creditsInfo.column.standard': 'Standard',
  'creditsInfo.column.advanced': 'Advanced',
  'creditsInfo.column.mastery': 'Mastery',
  'creditsInfo.badge.pro': 'PRO',
  'creditsInfo.action.generate': 'Generate document', // mockup-33: row label verbatim
  'creditsInfo.action.expand': 'Expand', // mockup-33: row label verbatim
  'creditsInfo.action.difficultyElevate': 'Elevate difficulty',
  'creditsInfo.action.translate': 'Translate',
  'creditsInfo.action.illustrate': 'Illustrate', // mockup-33: row label verbatim
  'creditsInfo.buy': 'Buy credits',
  // Public /pricing/credits (F48 — route is NOT auth-gated): logged-out
  // visitors see this CTA instead of the checkout grid.
  'creditsInfo.loginToBuy.title': 'Ready to buy credits?',
  'creditsInfo.loginToBuy.description': 'Log in or create a free account to pick a credit pack.',
  'creditsInfo.loginToBuy.cta': 'Log in to buy credits',
  // Public /pricing/credits landing-style chrome — the SPA has no marketing
  // shell, so the route carries its own header/footer (SiteHeader/SiteFooter
  // in apps/landing are the visual reference).
  'creditsInfo.header.login': 'Log in',
  'creditsInfo.header.startFree': 'Start free',
  'creditsInfo.header.openApp': 'Open app',
  // "How the credits system works" explainer cards. The counts arrive as ICU
  // vars from CreditsExplainerPage, which mirrors the real server constants
  // (SIGNUP_GRANT_CREDITS / SHARE_PUBLIC_REWARD_CREDITS / REFERRAL_REWARD_CREDITS).
  'creditsInfo.explainer.start.title': '{count, plural, one {Start with # free credit} other {Start with # free credits}}',
  'creditsInfo.explainer.start.body': 'Every new account starts with {count, plural, one {# credit} other {# credits}} — no card required.',
  'creditsInfo.explainer.spend.title': 'Spend on AI actions',
  'creditsInfo.explainer.spend.body': 'Each action costs a base amount plus a per-unit rate scaled by its complexity tier. A confirm dialog shows the exact cost and your balance before and after — nothing runs until you approve, and jobs debit when they are enqueued.',
  'creditsInfo.explainer.earn.title': 'Earn credits back',
  'creditsInfo.explainer.earn.body': 'Get {shareReward, plural, one {+# credit} other {+# credits}} when a public share is approved, and {referralReward, plural, one {+# credit} other {+# credits}} for every referred friend who signs up.',
  'creditsInfo.explainer.pro.title': 'Go PRO or top up',
  'creditsInfo.explainer.pro.body': 'PRO unlocks the PRO-locked actions — Expand, Elevate difficulty, Translate, and Illustrate. Credit packs top up your balance anytime.',
  'creditsInfo.footer.copyright': '© {year} xDocs',
  'creditsInfo.footer.nav': 'Legal',
  'creditsInfo.footer.terms': 'Terms',
  'creditsInfo.footer.privacy': 'Privacy',
  'creditsInfo.footer.contact': 'Contact',

  'action.retry': 'Retry',
} as const;

const ro: Record<keyof typeof en, string> = {
  'page.title': 'Plan și facturare',
  'page.description': 'Gestionează-ți planul, creditele și istoricul de plăți.',

  'plan.title': 'Planul tău',
  'plan.free.title': 'Ești pe Free',
  'plan.pro.title': 'Ești pe PRO',
  'plan.pro.renews': 'Se reînnoiește {date}',
  'plan.pro.cancelsAt': 'PRO până la {date}, apoi treci pe Free',
  'plan.pastDue.title': 'Plată restantă',
  'plan.pastDue.description': 'Plata ta PRO nu a trecut — actualizează metoda de plată până la {date} pentru a păstra PRO.',
  'plan.pastDue.descriptionNoDate': 'Plata ta PRO nu a trecut — actualizează metoda de plată pentru a păstra PRO.',
  'plan.status.loading': 'Se încarcă planul tău…',
  'plan.status.error.title': 'Nu am putut încărca planul tău.',
  'plan.action.upgrade': 'Treci la PRO',
  'plan.action.manage': 'Gestionează abonamentul',
  'plan.action.cancel': 'Anulează PRO',
  'plan.action.reactivate': 'Reactivează',
  'plan.action.updatePayment': 'Actualizează metoda de plată',
  'plan.checkoutError': 'Nu am putut începe plata. Încearcă din nou.',

  'cancel.title': 'Anulează PRO',
  'cancel.body': 'Vei păstra accesul PRO până la {date}. După aceea, contul tău trece pe Free și funcțiile exclusive PRO (extindere nelimitată, taie-și-continuă, creștere dificultate, traducere, ilustrații) vor fi blocate.',
  'cancel.bodyNoDate': 'Contul tău va trece pe Free la finalul perioadei curente de facturare. Funcțiile exclusive PRO vor fi blocate atunci.',
  'cancel.keepPro': 'Păstrează PRO',
  'cancel.confirm': 'Anulează PRO',
  'cancel.error': 'Nu am putut anula abonamentul tău. Încearcă din nou.',
  'cancel.toast.action': 'Anulat',

  'credits.title': 'Credite',
  'credits.balance.label': 'Sold',
  'credits.balance.low': 'Soldul tău este pe terminate.',
  'credits.balance.zero': 'Ai rămas fără credite — cumpără mai multe pentru a continua generarea.',
  'credits.ledger.title': 'Registru',
  'credits.ledger.column.date': 'Dată',
  'credits.ledger.column.reason': 'Motiv',
  'credits.ledger.column.delta': 'Sumă',
  'credits.ledger.column.balance': 'Sold',
  'credits.ledger.empty.title': 'Nicio activitate de credite încă',
  'credits.ledger.empty.description': 'Generările, achizițiile și recompensele vor apărea aici.',
  'credits.ledger.noResults.title': 'Nicio activitate nu se potrivește cu „{query}”',
  'credits.ledger.error.title': 'Nu am putut încărca registrul tău de credite.',
  'credits.ledger.reason.action_debit': 'Generare',
  'credits.ledger.reason.policy_refund': 'Rambursare',
  'credits.ledger.reason.purchase': 'Achiziție',
  'credits.ledger.reason.admin_grant': 'Acordare',
  'credits.ledger.reason.share_approved': 'Partajare aprobată',
  'credits.ledger.reason.share_public_reward': 'Recompensă partajare',
  'credits.ledger.reason.referral_reward': 'Recompensă recomandare',
  'credits.ledger.reason.promo_bonus': 'Bonus promo',
  'credits.ledger.reason.signup_grant': 'Credite de bun venit',

  'packs.title': 'Cumpără credite',
  'packs.status.loading': 'Se încarcă pachetele de credite…',
  'packs.status.error.title': 'Nu am putut încărca pachetele de credite.',
  'packs.status.empty.title': 'Niciun pachet de credite disponibil momentan',
  'packs.buy': 'Cumpără',
  'packs.buying': 'Se pornește plata…',

  'promo.label': 'Ai un cod?',
  'promo.placeholder': 'Introdu codul promoțional',
  'promo.apply': 'Aplică',
  'promo.remove': 'Elimină',
  'promo.validating': 'Se verifică codul…',
  'promo.applied.percent': '−{percent}% aplicat',
  'promo.applied.fixed': '−{amount} aplicat',
  'promo.applied.bonus': '+{count, plural, one {# credit bonus} few {# credite bonus} other {# de credite bonus}} aplicat',
  'promo.error.required': 'Introdu mai întâi un cod.',

  'invoices.title': 'Facturi',
  'invoices.column.date': 'Dată',
  'invoices.column.amount': 'Sumă',
  'invoices.column.status': 'Stare',
  'invoices.column.download': 'Descarcă',
  'invoices.empty.title': 'Nicio factură încă',
  'invoices.noResults.title': 'Nicio factură nu se potrivește cu „{query}”',
  'invoices.error.title': 'Nu am putut încărca facturile tale.',
  'invoices.download': 'Descarcă',
  'invoices.lockedForFree.title': 'Facturile sunt disponibile pe PRO',
  'invoices.lockedForFree.description': 'Treci la PRO pentru a-ți vedea istoricul de facturare.',
  'invoices.status.paid': 'Plătită',
  'invoices.status.open': 'Deschisă',
  'invoices.status.void': 'Anulată',
  'invoices.status.refunded': 'Rambursată',

  // --- F48 credits-info explainer (design/app/33-credits-info.html) ---
  'creditsInfo.trigger': 'Cum funcționează creditele',
  'creditsInfo.eyebrow': 'Prețuri',
  'creditsInfo.title': 'Cum funcționează creditele',
  'creditsInfo.description': 'Fiecare acțiune AI costă o sumă de bază plus o rată per unitate, scalată în funcție de complexitate. Vezi mereu costul înainte să confirmi.',
  'creditsInfo.column.action': 'Acțiune',
  'creditsInfo.column.standard': 'Standard',
  'creditsInfo.column.advanced': 'Avansat',
  'creditsInfo.column.mastery': 'Măiestrie',
  'creditsInfo.badge.pro': 'PRO',
  'creditsInfo.action.generate': 'Generează document',
  'creditsInfo.action.expand': 'Extinde',
  'creditsInfo.action.difficultyElevate': 'Crește dificultatea',
  'creditsInfo.action.translate': 'Traduce',
  'creditsInfo.action.illustrate': 'Ilustrează',
  'creditsInfo.buy': 'Cumpără credite',
  'creditsInfo.loginToBuy.title': 'Gata să cumperi credite?',
  'creditsInfo.loginToBuy.description': 'Autentifică-te sau creează un cont gratuit ca să alegi un pachet de credite.',
  'creditsInfo.loginToBuy.cta': 'Autentifică-te pentru a cumpăra credite',
  'creditsInfo.header.login': 'Autentificare',
  'creditsInfo.header.startFree': 'Începe gratuit',
  'creditsInfo.header.openApp': 'Deschide aplicația',
  'creditsInfo.explainer.start.title': '{count, plural, one {Începe cu # credit gratuit} few {Începe cu # credite gratuite} other {Începe cu # de credite gratuite}}',
  'creditsInfo.explainer.start.body': 'Fiecare cont nou pornește cu {count, plural, one {# credit} few {# credite} other {# de credite}} — fără card.',
  'creditsInfo.explainer.spend.title': 'Cheltuie pe acțiuni AI',
  'creditsInfo.explainer.spend.body': 'Fiecare acțiune costă o sumă de bază plus o rată per unitate, în funcție de nivelul de complexitate. Un dialog de confirmare arată costul exact și soldul înainte și după — nimic nu rulează până nu aprobi, iar joburile debitează creditele la punerea în coadă.',
  'creditsInfo.explainer.earn.title': 'Câștigă credite înapoi',
  'creditsInfo.explainer.earn.body': 'Primești {shareReward, plural, one {+# credit} few {+# credite} other {+# de credite}} când o partajare publică este aprobată și {referralReward, plural, one {+# credit} few {+# credite} other {+# de credite}} pentru fiecare prieten recomandat care își creează cont.',
  'creditsInfo.explainer.pro.title': 'Treci la PRO sau reîncarcă',
  'creditsInfo.explainer.pro.body': 'PRO deblochează acțiunile exclusive PRO — Extinde, Crește dificultatea, Traduce și Ilustrează. Pachetele de credite îți reîncarcă soldul oricând.',
  'creditsInfo.footer.copyright': '© {year} xDocs',
  'creditsInfo.footer.nav': 'Legal',
  'creditsInfo.footer.terms': 'Termeni',
  'creditsInfo.footer.privacy': 'Confidențialitate',
  'creditsInfo.footer.contact': 'Contact',

  'action.retry': 'Reîncearcă',
};

export const SETTINGS_BILLING_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type SettingsBillingCatalogKey = keyof typeof en;
